import { useState } from "react";
import axios from "axios";
import { X, Plus } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Lightweight modal for creating a new Part from the inline "+ Add part" button
 * on a product-class group header.
 *
 * Props:
 *   preset: { product_class, product_type, apply_to }
 *   onClose: () => void
 *   onSaved: () => void  (called after a successful create so the table reloads)
 */
export default function AddPartModal({ preset, onClose, onSaved, userEmail = "" }) {
  const [form, setForm] = useState({
    product_name: "",
    product_class: preset?.product_class || "",
    product_type: preset?.product_type || "",
    spec1: "",
    list_price: "",
    list_price_ca: "",
    explanation: "",
    apply_to: preset?.apply_to || "us",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.product_name.trim()) {
      setErr("Product Name is required."); return;
    }
    // For apply_to=us, the name must start with US; for acc → ACC; for both → either (server rewrites).
    const n = form.product_name.trim().toUpperCase();
    if (form.apply_to === "us" && !n.startsWith("US")) {
      setErr("Product Name must start with 'US' for a US-only part."); return;
    }
    if (form.apply_to === "acc" && !n.startsWith("ACC")) {
      setErr("Product Name must start with 'ACC' for an ACC-only part."); return;
    }

    const payload = {
      product_name: form.product_name.trim(),
      product_class: form.product_class || "",
      product_type: form.product_type || "",
      spec1: form.spec1 || "",
      explanation: form.explanation || "",
      apply_to: form.apply_to,
    };
    if (form.list_price !== "") payload.list_price = parseFloat(form.list_price);
    if (form.list_price_ca !== "") payload.list_price_ca = parseFloat(form.list_price_ca);

    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/parts`, payload, {
        withCredentials: true,
        headers: userEmail ? { "X-User-Email": userEmail } : undefined,
      });
      onSaved?.();
      onClose?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const RadioOption = ({ value, label, hint }) => {
    const active = form.apply_to === value;
    return (
      <label
        data-testid={`add-part-apply-${value}`}
        className={`flex-1 cursor-pointer border rounded-sm px-3 py-2.5 text-sm transition-colors ${
          active ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-blue-300"
        }`}
      >
        <input type="radio" name="apply_to" value={value} checked={active}
          onChange={(e) => setForm((f) => ({ ...f, apply_to: e.target.value }))} className="sr-only" />
        <div className={`font-bold ${active ? "text-blue-700" : "text-slate-700"}`}>{label}</div>
        <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>
      </label>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="add-part-modal">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white border-l border-slate-200 shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold tracking-tight font-['Chivo'] flex items-center gap-2">
            <Plus size={18} weight="bold" className="text-blue-600" /> Add part
          </h2>
          <button data-testid="add-part-close" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>

        {preset?.product_class && (
          <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Product Class</div>
            <div className="text-sm font-semibold text-slate-900">{preset.product_class}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {err && (
            <div data-testid="add-part-error" className="bg-red-50 text-red-700 text-sm px-4 py-2 rounded-sm border border-red-200">
              {err}
            </div>
          )}

          {/* Apply to brand */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Apply to</label>
            <div className="flex gap-2">
              <RadioOption value="us" label="US only" hint="Creates US-prefixed SKU" />
              <RadioOption value="acc" label="ACC only" hint="Creates ACC-prefixed SKU" />
              <RadioOption value="both" label="Both" hint="Creates US + ACC pair" />
            </div>
          </div>

          <FieldRow label="Product Name" testId="add-part-name" required
            value={form.product_name} onChange={set("product_name")}
            placeholder={form.apply_to === "acc" ? "e.g. ACC75/D55" : "e.g. US75/D55"} />

          <FieldRow label="Product Class" testId="add-part-class"
            value={form.product_class} onChange={set("product_class")} placeholder="e.g. Screw Compressor" />

          <FieldRow label="Product Type" testId="add-part-type"
            value={form.product_type} onChange={set("product_type")} placeholder="e.g. Compressor / Air Dryer / Tank" />

          <FieldRow label="Voltage / Spec1" testId="add-part-spec"
            value={form.spec1} onChange={set("spec1")} placeholder="e.g. 480V / 3Phz" />

          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="List Price (USD)" testId="add-part-price-us" type="number"
              value={form.list_price} onChange={set("list_price")} placeholder="0.00" />
            <FieldRow label="List Price (CAD)" testId="add-part-price-ca" type="number"
              value={form.list_price_ca} onChange={set("list_price_ca")} placeholder="0.00" />
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Notes / Explanation</label>
            <textarea data-testid="add-part-notes" rows="3" value={form.explanation} onChange={set("explanation")}
              className="w-full rounded-sm border border-slate-300 px-3 py-2 text-sm" placeholder="Optional details shown on quotes / exports" />
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
            <Button data-testid="add-part-save" type="submit" disabled={saving}
              className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-4 text-sm font-semibold">
              {saving ? "Creating…" : (form.apply_to === "both" ? "Create US + ACC" : `Create ${form.apply_to.toUpperCase()} part`)}
            </Button>
            <Button data-testid="add-part-cancel" type="button" onClick={onClose}
              className="rounded-sm bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FieldRow({ label, testId, value, onChange, type = "text", placeholder, required }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
        {label}{required ? " *" : ""}
      </label>
      <Input data-testid={testId} type={type} value={value ?? ""} onChange={onChange} placeholder={placeholder}
        className="rounded-sm border-slate-300 h-9 text-sm font-mono" />
    </div>
  );
}
